/// <reference path="../../polaris-web-components.d.ts" />
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { environment } from '../Services/environment';
import { getShopUrl } from '../Services/CommonServices';

const endpoint = environment.serverUrl;

export function CollectionRuleConditionsModal({ isOpen, onClose }) {
    const [ruleConditions, setRuleConditions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const storeUrl = getShopUrl();
    const modalRef = useRef(null);

    // Show/hide modal using the documented showOverlay/hideOverlay methods
    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;

        if (isOpen) {
            fetchRuleConditions();
            modal.showOverlay();

            const handleHide = (e) => {
                e.stopPropagation();
                if (onClose) onClose();
            };
            modal.addEventListener('hide', handleHide);
            return () => modal.removeEventListener('hide', handleHide);
        } else {
            modal.hideOverlay();
        }
    }, [isOpen]);

    const fetchRuleConditions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${endpoint}/api/Collection/rule-conditions`, {
                params: { storeUrl }
            });

            if (response.data.success) {
                setRuleConditions(response.data.data || []);
            } else {
                setError('Failed to load rule conditions');
            }
        } catch (err) {
            console.error('Error fetching rule conditions:', err);
            setError('Error loading rule conditions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatRuleType = (ruleType) => {
        return ruleType
            .split('_')
            .map(word => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    };

    return (
        <s-modal
            ref={modalRef}
            id="collection-rule-conditions-modal"
            heading="Valid Collection Rule Conditions"
            size="base"
            padding="none"
        >
            <s-box padding="base">
                <s-text as="p" tone="subdued">
                    Reference guide for collection rule types and their allowed relations
                </s-text>
            </s-box>

            {loading && (
                <s-box padding="large">
                    <s-stack alignment="center">
                        <s-spinner size="large" />
                        <s-text>Loading rule conditions...</s-text>
                    </s-stack>
                </s-box>
            )}

            {error && (
                <s-box padding="base">
                    <s-banner tone="critical">{error}</s-banner>
                </s-box>
            )}

            {!loading && !error && ruleConditions.length > 0 && (
                <s-table>
                    <s-table-header-row>
                        <s-table-header>Rule Type</s-table-header>
                        <s-table-header>Allowed Relations</s-table-header>
                    </s-table-header-row>
                    <s-table-body>
                        {ruleConditions.map((rule, index) => (
                            <s-table-row key={index}>
                                <s-table-cell>
                                    <s-text type="strong">{formatRuleType(rule.RuleType || rule.ruleType)}</s-text>
                                </s-table-cell>
                                <s-table-cell>
                                    <s-stack direction="inline" gap="small" wrap>
                                        {(rule.AllowedRelations || rule.allowedRelations || []).map((relation, idx) => (
                                            <s-badge key={idx} tone="info">
                                                {relation}
                                            </s-badge>
                                        ))}
                                    </s-stack>
                                </s-table-cell>
                            </s-table-row>
                        ))}
                    </s-table-body>
                </s-table>
            )}

            {!loading && !error && ruleConditions.length === 0 && (
                <s-box padding="large">
                    <s-stack alignment="center">
                        <s-text tone="subdued">No rule conditions available</s-text>
                    </s-stack>
                </s-box>
            )}

            <s-button
                slot="secondary-actions"
                variant="secondary"
                commandFor="collection-rule-conditions-modal"
                command="--hide"
            >
                Close
            </s-button>
        </s-modal>
    );
}
